import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tv } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PERIODOS, isPeriodo, useIndicadores, useMapa, useSncr, type Periodo } from "./api";
import { formatNumero, useCidadesResolvidas, type Metrica } from "./cidades";
import { FarmaciasPendentes } from "./FarmaciasPendentes";
import { FilaLocalizacao } from "./FilaLocalizacao";
import { KpiCards } from "./KpiCards";
import { MapaCidades } from "./MapaCidades";
import { SncrCards } from "./SncrCards";
import { TopCidades } from "./TopCidades";
import { entrarEmTelaCheia } from "@/lib/telaCheia";

export default function DashboardSilpiFarma() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const periodoUrl = searchParams.get("periodo");
  const periodo: Periodo = isPeriodo(periodoUrl) ? periodoUrl : "tudo";
  const [metrica, setMetrica] = useState<Metrica>("dispensacoes");

  const indicadores = useIndicadores(periodo);
  const mapa = useMapa(periodo);
  const sncr = useSncr();
  const { cidades, naoEncontradas, isLoading: carregandoMunicipios } = useCidadesResolvidas(mapa.data);
  const carregandoMapa = mapa.isLoading || carregandoMunicipios;

  const semLocalizacao =
    metrica === "dispensacoes" ? mapa.data?.dispensacoesSemLocalizacao : mapa.data?.farmaciasSemLocalizacao;

  function exibirNaTv() {
    // Tela cheia precisa partir do clique (gesto do usuário); a troca de rota é
    // SPA, então o documento continua em tela cheia na página da TV.
    void entrarEmTelaCheia();
    navigate(`/SilpiFarma/Dashboard/TV?periodo=${periodo}`);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-foreground">Silpi Farma</h1>
        <div className="flex items-center gap-2">
          <Select
            value={periodo}
            onValueChange={(v) => isPeriodo(v) && setSearchParams(v === "tudo" ? {} : { periodo: v }, { replace: true })}
          >
            <SelectTrigger className="w-48" aria-label="Período">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODOS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exibirNaTv}>
            <Tv className="mr-2 h-4 w-4" aria-hidden /> Exibir na TV
          </Button>
        </div>
      </div>

      <KpiCards data={indicadores.data} isLoading={indicadores.isLoading} />

      <section aria-labelledby="titulo-sncr" className="space-y-2">
        <h2 id="titulo-sncr" className="text-sm font-medium text-muted-foreground">
          SNCR <span className="font-normal">· total, não segue o período</span>
        </h2>
        <SncrCards data={sncr.data} isLoading={sncr.isLoading} />
      </section>

      <div className="grid gap-4 lg:grid-cols-3">
        <section className="flex flex-col rounded-xl border border-border bg-card p-5 shadow-card lg:col-span-2">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-foreground">Cidades</h2>
            <ToggleGroup
              type="single"
              size="sm"
              variant="outline"
              value={metrica}
              onValueChange={(v) => v && setMetrica(v as Metrica)}
            >
              <ToggleGroupItem value="dispensacoes">Dispensações</ToggleGroupItem>
              <ToggleGroupItem value="farmacias">Farmácias cadastradas</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="relative z-0 h-[480px]">
            {carregandoMapa ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <MapaCidades cidades={cidades} metrica={metrica} />
            )}
          </div>
          {(!!semLocalizacao || naoEncontradas > 0) && (
            <p className="mt-2 text-xs text-muted-foreground">
              {!!semLocalizacao &&
                `${formatNumero(semLocalizacao)} ${metrica === "dispensacoes" ? "dispensação(ões)" : "farmácia(s)"} sem município — ver "Localização de CNPJs" abaixo.`}
              {naoEncontradas > 0 && ` ${naoEncontradas} código(s) IBGE fora da tabela local de municípios.`}
            </p>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card p-5 shadow-card">
          <h2 className="mb-3 text-base font-semibold text-foreground">
            Top cidades · {metrica === "dispensacoes" ? "dispensações" : "farmácias"}
          </h2>
          {carregandoMapa ? <Skeleton className="h-80 w-full" /> : <TopCidades cidades={cidades} metrica={metrica} />}
        </section>
      </div>

      <FarmaciasPendentes />

      <FilaLocalizacao />
    </div>
  );
}
